import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  ArrowLeftRight, CheckCircle2, XCircle, RefreshCw, Clock, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FILTERS = [
  { v: 'pending',   label: 'Pending',   bg: 'bg-amber-100 text-amber-700' },
  { v: 'approved',  label: 'Approved',  bg: 'bg-green-100 text-green-700' },
  { v: 'rejected',  label: 'Rejected',  bg: 'bg-red-100 text-red-700' },
  { v: 'cancelled', label: 'Cancelled', bg: 'bg-gray-100 text-gray-700' },
  { v: '',          label: 'All',       bg: '' },
];

export const CrmShiftSwaps = () => {
  const [swaps, setSwaps] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionSwap, setActionSwap] = useState(null);
  const [actionMode, setActionMode] = useState(null); // 'approve' | 'reject'
  const [recipient, setRecipient] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchSwaps = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const r = await axios.get(`${API_URL}/api/admin/shift-swaps`,
        { params, withCredentials: true });
      setSwaps(r.data?.items || []);
    } catch (e) {
      toast.error('Failed to load swaps');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchSwaps(); }, [fetchSwaps]);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/employees`, { withCredentials: true })
      .then((r) => setEmployees((r.data || []).filter((e) => e.active !== false)))
      .catch(() => setEmployees([]));
  }, []);

  const openApprove = (swap) => {
    setActionSwap(swap);
    setActionMode('approve');
    setRecipient(swap.proposed_recipient_id || 'none');
    setNote('');
  };

  const openReject = (swap) => {
    setActionSwap(swap);
    setActionMode('reject');
    setRecipient('');
    setNote('');
  };

  const submit = async () => {
    if (!actionSwap || !actionMode) return;
    setSubmitting(true);
    try {
      const url = `${API_URL}/api/admin/shift-swaps/${actionSwap.swap_id}/${actionMode}`;
      const body = actionMode === 'approve'
        ? { recipient_id: recipient && recipient !== 'none' ? recipient : null, note }
        : { note };
      await axios.post(url, body, { withCredentials: true });
      toast.success(actionMode === 'approve' ? 'Swap approved & shift reassigned' : 'Swap rejected');
      setActionSwap(null);
      setActionMode(null);
      fetchSwaps();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setSubmitting(false); }
  };

  const statusPill = (s) => {
    const meta = STATUS_FILTERS.find((f) => f.v === s) || STATUS_FILTERS[0];
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.bg}`}>{meta.label}</span>;
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;

  return (
    <div data-testid="crm-shift-swaps">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <ArrowLeftRight className="inline w-7 h-7 mr-2 text-[#FF3B6B]" />Shift Swaps
          </h1>
          <p className="text-[#6B6B7A]">Approve or reject employee swap requests. Approving reassigns the shift automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.v || 'all'}
              variant={statusFilter === f.v ? 'default' : 'outline'}
              className={`rounded-full text-xs ${statusFilter === f.v ? 'bg-[#1E1E24] text-white' : 'border-[#E8E5DF]'}`}
              onClick={() => setStatusFilter(f.v)}
              data-testid={`swap-filter-${f.v || 'all'}`}
            >{f.label}</Button>
          ))}
          <Button variant="outline" onClick={fetchSwaps} className="rounded-full" data-testid="swap-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden" data-testid="swap-list">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Requested</TableHead>
              <TableHead>Requester</TableHead>
              <TableHead>Shift</TableHead>
              <TableHead>Proposed Recipient</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {swaps.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#6B6B7A]">No swap requests found.</TableCell></TableRow>
            ) : swaps.map((s) => (
              <TableRow key={s.swap_id} data-testid={`swap-row-${s.swap_id}`}>
                <TableCell className="text-xs text-[#6B6B7A]">
                  {s.created_at ? new Date(s.created_at).toLocaleString() : '—'}
                </TableCell>
                <TableCell>
                  <p className="text-sm font-medium">{s.requester_name}</p>
                  <p className="text-[11px] text-[#6B6B7A]">{s.requester_email}</p>
                </TableCell>
                <TableCell className="text-sm">
                  <p className="font-medium">{s.shift_date}</p>
                  <p className="text-[11px] text-[#6B6B7A] font-mono">{s.shift_start}–{s.shift_end}</p>
                  {s.shift_role && <p className="text-[11px] text-[#6B6B7A] capitalize">{s.shift_role.replace(/_/g, ' ')}</p>}
                </TableCell>
                <TableCell className="text-sm">
                  {s.approved_recipient_name || s.proposed_recipient_name || <span className="text-[#6B6B7A] italic">Anyone</span>}
                </TableCell>
                <TableCell className="text-xs text-[#6B6B7A] max-w-[240px] truncate" title={s.reason || ''}>
                  {s.reason || '—'}
                </TableCell>
                <TableCell>
                  {statusPill(s.status)}
                  {s.manager_note && <p className="text-[10px] text-[#6B6B7A] italic mt-1">"{s.manager_note}"</p>}
                </TableCell>
                <TableCell className="text-right">
                  {s.status === 'pending' ? (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" className="bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full"
                        onClick={() => openApprove(s)}
                        data-testid={`swap-approve-${s.swap_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => openReject(s)}
                        data-testid={`swap-reject-${s.swap_id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                  ) : s.resolved_at ? (
                    <span className="text-[11px] text-[#6B6B7A]"><Clock className="w-3 h-3 inline mr-1" />{new Date(s.resolved_at).toLocaleString()}</span>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Approve / Reject dialog */}
      <Dialog open={!!actionSwap} onOpenChange={(o) => { if (!o) { setActionSwap(null); setActionMode(null); } }}>
        <DialogContent className="bg-white max-w-md" data-testid="swap-action-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionMode === 'approve' ? (
                <><CheckCircle2 className="w-5 h-5 text-[#06D6A0]" /> Approve Swap</>
              ) : (
                <><XCircle className="w-5 h-5 text-red-500" /> Reject Swap</>
              )}
            </DialogTitle>
            <DialogDescription>
              {actionMode === 'approve'
                ? 'Reassign the shift to the recipient, or leave unassigned to mark it open for pickup.'
                : 'Tell the requester why this swap can\'t happen. They will see your note in their portal.'}
            </DialogDescription>
          </DialogHeader>
          {actionSwap && (
            <div className="rounded-xl border border-[#E8E5DF] bg-[#FFFDF8] px-3 py-2 text-sm">
              <p className="font-semibold text-[#1E1E24]">{actionSwap.requester_name} · {actionSwap.shift_date} · {actionSwap.shift_start}–{actionSwap.shift_end}</p>
              {actionSwap.reason && <p className="text-xs text-[#6B6B7A] mt-1">{actionSwap.reason}</p>}
            </div>
          )}
          {actionMode === 'approve' && (
            <div>
              <Label className="text-xs">Reassign to</Label>
              <Select value={recipient} onValueChange={setRecipient}>
                <SelectTrigger className="bg-white mt-1" data-testid="swap-action-recipient">
                  <SelectValue placeholder="Select an employee" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-[260px]">
                  <SelectItem value="none">Leave unassigned (open for pickup)</SelectItem>
                  {employees.filter((e) => e.user_id !== actionSwap?.requester_id).map((e) => (
                    <SelectItem key={e.user_id} value={e.user_id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Note to requester (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1"
              placeholder={actionMode === 'approve' ? 'e.g. Reassigned to Mark — thanks for the heads up!' : 'e.g. Sorry, no coverage available this date.'}
              data-testid="swap-action-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setActionSwap(null); setActionMode(null); }}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={submit} disabled={submitting}
              className={`rounded-full text-white ${actionMode === 'approve' ? 'bg-[#06D6A0] hover:bg-[#05B88A]' : 'bg-red-500 hover:bg-red-600'}`}
              data-testid="swap-action-submit">
              {submitting ? 'Working…' : (actionMode === 'approve' ? 'Approve & Reassign' : 'Reject Swap')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmShiftSwaps;
